import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Plus } from "lucide-react";
import { Pagination, RoleValidator } from "../../../Components/Common";
import { CREATE_SITE, SITE_DETAILS } from "../../../routes";
import { ROLES } from "../../../Constants/Strings";
import { useProjects } from "../Hooks/useProjects";

const SitesTab = ({ projectId }) => {
  const navigate = useNavigate();

  const {
    projectSites,
    fetchProjectSites,
    totalProjectSites,
    page,
    rowsPerPage,
    totalPages,
    onPageChange,
    onPageSizeChange,
    loading,
  } = useProjects({ projectId });

  useEffect(() => {
    if (projectId) {
      fetchProjectSites({ sites: "all" });
    }
  }, [projectId, page, rowsPerPage]);

  const handleRowClick = (siteId) => {
    navigate(SITE_DETAILS.replace(":instanceId", siteId));
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <span className="flex justify-between items-center font-bold px-2 pt-1 pb-3 ">
        <h3 className="text-lg font-semibold text-primary">
          Sites ({totalProjectSites})
        </h3>
        <div className="flex items-center gap-3">
          <RoleValidator
            allowedRoles={[
              ROLES.MANAGING_DIRECTOR,
              ROLES.HEAD_OFFICE,
              ROLES.GOVERNMENT_OFFICER,
              ROLES.PROJECT_MANAGER,
              ROLES.PROJECT_ENGINEER,
            ]}
            mode="disable"
          >
            <button
              className="flex items-center px-3 py-1 rounded-md text-sm bg-primary text-white hover:bg-secondary transition-colors"
              onClick={() => navigate(CREATE_SITE)}
            >
              <Plus size={18} className="inline mr-2" /> Add Site
            </button>
          </RoleValidator>
        </div>
      </span>

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {loading ? (
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div>
          </div>
        ) : projectSites.length === 0 ? (
          <div className="text-center text-gray-500 py-6">No sites found.</div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700 w-full">
            {projectSites.map((site) => (
              <li
                className="p-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                key={site.id}
              >
                <div className="grid grid-cols-5 items-center">
                  <div
                    className="col-span-3"
                    onClick={() => handleRowClick(site.id)}
                  >
                    <p className="text-sm font-medium dark:text-white">
                      {site.site_name}
                    </p>

                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate-[90%]">
                      {site.region ? `${site.region}, ` : ""}
                      {site.zone ? `${site.zone}, ` : ""}
                      {site.district ? `${site.district}, ` : ""}
                      {site.state}
                    </p>
                  </div>

                  <div className="col-span-1 justify-center items-center">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {site.completion_percentage || 0}%
                    </span>
                  </div>

                  <div
                    className="col-span-1 flex justify-center items-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <a
                      href={site.location_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`p-2 rounded-full hover:bg-gray-100 ${
                        site.location_url ? "text-blue-600" : "text-gray-200"
                      }`}
                    >
                      <MapPin size={18} />
                    </a>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="mb-2">
        <Pagination
          page={page}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onPageSizeChange={(newSize) => {
            onPageSizeChange(newSize);
          }}
        />
      </div>
    </div>
  );
};

export default SitesTab;
